import { Observable } from 'rxjs';
import { PagedResultDto } from '@abp/ng.core';
import {
  ChangePlanningItemStatusDto,
  CreatePlanningGroupDto,
  PlanningBacklogRequestDto,
  PlanningBoardDto,
  PlanningBoardMetaDto,
  PlanningBoardRequestDto,
  PlanningGroupDto,
  PlanningItemDto,
  PlanPlanningItemDto,
  UnplanPlanningItemDto,
  UpdatePlanningGroupDto,
} from '@digiwall/goliath-documents/proxy';
import { toIsoDay } from '../planning-scale';
import {
  PlanningChangeStatusRequest,
  PlanningCreateGroupRequest,
  PlanningDataSourceAdapter,
  PlanningDeleteGroupRequest,
  PlanningPeriodQuery,
  PlanningPlanRequest,
  PlanningUnplanRequest,
  PlanningUpdateGroupRequest,
} from './planning-adapter';

/** Backlog page size — the initial fetch and the increment each "load more" adds to the single page. */
export const BACKLOG_PAGE_SIZE = 100;

/**
 * Structural shape every generated per-source planning proxy service satisfies — they all implement
 * IPlanningDataSourceAppService, so their method names and signatures are identical. This lets one base
 * adapter drive any data source's service without the generated proxies sharing a TypeScript interface.
 */
export interface PlanningDataSourceApi {
  getBoard(input: PlanningBoardRequestDto): Observable<PlanningBoardDto>;
  getBacklog(input: PlanningBacklogRequestDto): Observable<PagedResultDto<PlanningItemDto>>;
  getBoardMeta(planningConfigurationId: string): Observable<PlanningBoardMetaDto>;
  plan(input: PlanPlanningItemDto): Observable<PlanningItemDto>;
  unplan(input: UnplanPlanningItemDto): Observable<void>;
  changeStatus(input: ChangePlanningItemStatusDto): Observable<PlanningItemDto>;
  createGroup(input: CreatePlanningGroupDto): Observable<PlanningGroupDto>;
  updateGroup(id: string, input: UpdatePlanningGroupDto): Observable<PlanningGroupDto>;
  deleteGroup(id: string, planningConfigurationId: string): Observable<void>;
}

/**
 * Shared adapter behaviour: maps the shell's requests onto the data source's generated proxy service.
 * Concrete adapters only supply the data source id, the card component and (optionally) status
 * enumeration and deep-link navigation.
 */
export abstract class BasePlanningAdapter implements Partial<PlanningDataSourceAdapter> {
  protected constructor(protected readonly api: PlanningDataSourceApi) {}

  getBoardMeta(configId: string): Observable<PlanningBoardMetaDto> {
    return this.api.getBoardMeta(configId);
  }

  loadPlanned(query: PlanningPeriodQuery): Observable<PlanningBoardDto> {
    return this.api.getBoard(this.boardRequest(query));
  }

  loadBacklog(query: PlanningPeriodQuery, limit: number): Observable<PagedResultDto<PlanningItemDto>> {
    return this.api.getBacklog({
      planningConfigurationId: query.configId,
      maxResultCount: limit,
      skipCount: 0,
    } as PlanningBacklogRequestDto);
  }

  plan(request: PlanningPlanRequest): Observable<PlanningItemDto> {
    return this.api.plan(this.planRequest(request));
  }

  unplan(request: PlanningUnplanRequest): Observable<void> {
    return this.api.unplan({
      planningConfigurationId: request.configId,
      itemId: request.itemId,
    });
  }

  changeStatus(request: PlanningChangeStatusRequest): Observable<PlanningItemDto> {
    return this.api.changeStatus({
      planningConfigurationId: request.configId,
      itemId: request.itemId,
      statusId: request.statusId,
    });
  }

  createGroup(request: PlanningCreateGroupRequest): Observable<PlanningGroupDto> {
    return this.api.createGroup({
      planningConfigurationId: request.configId,
      date: request.date,
      label: request.label,
    });
  }

  updateGroup(request: PlanningUpdateGroupRequest): Observable<PlanningGroupDto> {
    return this.api.updateGroup(request.id, {
      planningConfigurationId: request.configId,
      label: request.label,
      date: request.date,
      order: request.order,
    });
  }

  deleteGroup(request: PlanningDeleteGroupRequest): Observable<void> {
    return this.api.deleteGroup(request.id, request.configId);
  }

  private boardRequest(query: PlanningPeriodQuery): PlanningBoardRequestDto {
    return {
      planningConfigurationId: query.configId,
      periodStart: toIsoDay(query.periodStart),
      range: query.range,
    };
  }

  private planRequest(request: PlanningPlanRequest): PlanPlanningItemDto {
    return {
      planningConfigurationId: request.configId,
      itemId: request.itemId,
      date: request.date,
      groupId: request.groupId,
      order: request.order,
    };
  }
}
